import { useState } from 'react';
import type { ChaseNotification, ChaseNotificationPayload } from '../types/connector.types';
import type { IncompleteTimesheetRow, UseIncompleteTimesheetsResult } from '../hooks/useIncompleteTimesheets';
import type { UseChaseNotificationsResult } from '../hooks/useChaseNotifications';
import { formatMonthLabel } from '../utils/dateUtils';

// ─── Icons ────────────────────────────────────────────────────────────────────

function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 12, height: 12, strokeWidth: 2 }}>
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 12, height: 12, strokeWidth: 2 }}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function getInitials(name: string): string {
  return name.split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase();
}

/** Returns true if this row already has a chase in history for its own period. */
function isNotified(row: IncompleteTimesheetRow, history: ChaseNotification[]): boolean {
  return history.some(h => h.personEmail === row.personEmail && h.periodMonth === row.rowPeriodMonth);
}

interface IncompleteTimesheetsSectionProps {
  incomplete: UseIncompleteTimesheetsResult;
  chases: UseChaseNotificationsResult;
}

export function IncompleteTimesheetsSection({
  incomplete,
  chases,
}: IncompleteTimesheetsSectionProps) {
  const [sendingIds, setSendingIds] = useState<Set<string>>(new Set());
  const [isBulkSending, setIsBulkSending] = useState(false);
  const [localError, setLocalError] = useState<string>('');

  const { rows, isLoading, error: loadError } = incomplete;
  const { history, sendChase, sendBulk } = chases;

  const totalCount = rows.length;
  const unnotifiedCount = rows.filter(r => !isNotified(r, history)).length;

  const buildPayload = (row: IncompleteTimesheetRow): ChaseNotificationPayload => ({
    personName: row.personName,
    personEmail: row.personEmail,
    periodMonth: row.rowPeriodMonth,
  });

  const handleNotifyPerson = async (row: IncompleteTimesheetRow) => {
    setLocalError('');
    setSendingIds(prev => new Set(prev).add(row.personId));
    try {
      await sendChase(buildPayload(row));
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Failed to send reminder.');
    } finally {
      setSendingIds(prev => {
        const next = new Set(prev);
        next.delete(row.personId);
        return next;
      });
    }
  };

  const handleNotifyAll = async () => {
    setLocalError('');
    const targets = rows.filter(r => !isNotified(r, history));
    if (targets.length === 0) return;
    setIsBulkSending(true);
    try {
      const payloads = targets.map(r => buildPayload(r));
      const { failed } = await sendBulk(payloads);
      if (failed.length > 0) {
        setLocalError(`${failed.length} reminder${failed.length === 1 ? '' : 's'} failed to send.`);
      }
    } finally {
      setIsBulkSending(false);
    }
  };

  // Header stays visible while data is loading so users see where the section sits.
  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 28,
          marginBottom: 10,
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
            Incomplete Timesheets
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {isLoading
              ? 'Loading non-submitters…'
              : `${totalCount} staff have not submitted — ${unnotifiedCount} not yet notified`}
          </div>
        </div>
        <button
          className="notify-btn"
          onClick={() => void handleNotifyAll()}
          disabled={isLoading || isBulkSending || unnotifiedCount === 0}
          title={unnotifiedCount === 0 ? 'Everyone has already been notified' : 'Send reminder to everyone not yet notified'}
        >
          {isBulkSending ? (
            <><span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />&nbsp;Sending…</>
          ) : (
            <><BellIcon />&nbsp;Notify All</>
          )}
        </button>
      </div>

      {(loadError || localError) && (
        <div className="err-banner" style={{ marginBottom: 12 }}>
          {loadError ? loadError.message : localError}
        </div>
      )}

      <div className="tbl-scroll-wrap">
      <div className="admin-hdr super-inc-grid">
        <div>Person</div>
        <div>M365 Email</div>
        <div>Line Manager</div>
        <div>Last Submitted</div>
        <div style={{ textAlign: 'right' }}>Action</div>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-dim)', fontSize: 13 }}>
          <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
        </div>
      ) : rows.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: 24,
            color: 'var(--text-dim)',
            fontSize: 13,
            background: '#fff',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r, 10px)',
          }}
        >
          All active staff have submitted timesheets for this period.
        </div>
      ) : (
        rows.map(row => {
          const notified = isNotified(row, history);
          const isSending = sendingIds.has(row.personId);
          return (
            <div
              key={row.personId}
              className={`admin-row super-inc-grid${notified ? ' done' : ''}`}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div className="pav blue">{getInitials(row.personName)}</div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div className="p-name" style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                      {row.personName}
                    </div>
                    <span style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: 'var(--warning, #b45309)',
                      background: 'var(--warning-bg, #fef3c7)',
                      border: '1px solid var(--warning-border, #fde68a)',
                      borderRadius: 4,
                      padding: '1px 5px',
                      whiteSpace: 'nowrap',
                    }}>
                      {formatMonthLabel(row.rowPeriodMonth)}
                    </span>
                  </div>
                  <div className="p-dept" style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                    {row.department}
                  </div>
                </div>
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--text-muted)',
                  fontFamily: "'DM Mono', monospace",
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {row.personEmail}
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--text)' }}>{row.lineManager}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  Last: {row.lastSubmittedLabel}
                </span>
                {row.daysOverdue > 0 && (
                  <span className="overdue-tag">{row.daysOverdue} days overdue</span>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                {notified ? (
                  <span className="notify-btn sent">
                    <CheckIcon />&nbsp;Notified
                  </span>
                ) : (
                  <button
                    className="notify-btn"
                    onClick={() => void handleNotifyPerson(row)}
                    disabled={isSending || isBulkSending}
                  >
                    {isSending ? (
                      <><span className="spinner" style={{ width: 10, height: 10, borderWidth: 2 }} />&nbsp;Sending…</>
                    ) : (
                      <><BellIcon />&nbsp;Notify</>
                    )}
                  </button>
                )}
              </div>
            </div>
          );
        })
      )}
      </div>{/* tbl-scroll-wrap */}
    </>
  );
}
